import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getCurrentUser, login as loginRequest, setAuthTokenProvider, setUnauthorizedHandler } from '../../api';
import type { AuthUser, UserRole } from '../../types';

const STORAGE_KEY = 'responsily.session';

interface StoredSession {
  user: AuthUser;
  /** the bearer token from POST /login/auth; null for a session saved before tokens existed */
  token: string | null;
  /** when the token stops working (ms since the epoch); null when the server sent no expiry */
  expiresAt: number | null;
}

/**
 * Read the saved session. A session whose token has expired is not restored (the server would answer 401 anyway), so the
 * user is sent to sign in again straight away instead of after a failed request.
 * The token lives in localStorage like the rest of the session: that survives a reload, but any script running on the
 * page could read it, so the page must never load untrusted scripts.
 */
const readSession = (): StoredSession | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || !parsed.user) return null;
    const expiresAt = typeof parsed.expiresAt === 'number' ? parsed.expiresAt : null;
    if (expiresAt !== null && expiresAt <= Date.now()) return null;
    return { user: parsed.user, token: typeof parsed.token === 'string' && parsed.token ? parsed.token : null, expiresAt };
  } catch (e) {
    return null; // storage unavailable or corrupt: treat as signed out
  }
};

const writeSession = (session: StoredSession | null) => {
  try {
    if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* storage unavailable: session lasts for this page load only */
  }
};

// The token the API client sends. Registered once here and read at request time, so a reload or a new sign-in needs no
// component changes.
let currentToken: string | null = null;
setAuthTokenProvider(() => currentToken);

interface LoginResult { ok: boolean; user?: AuthUser; message?: string }
interface AuthValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** user | admin | superadmin; undefined until known (older sessions get it from GET /users/me) */
  role: UserRole | undefined;
  /** admin or superadmin: shows the admin screens. The server is what actually enforces it; this only hides them. */
  isAdmin: boolean;
  /** true while an older session is still asking GET /users/me for its role */
  roleLoading: boolean;
  /** true after the server refused the token (401): the sign-in page says the session ended */
  sessionExpired: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Holds the signed-in user and their token. On sign-in the token is registered with `setAuthTokenProvider`
 * (src/api/client.js) so every request sends `Authorization: Bearer <token>`; sign-out clears it. Any 401 from the server
 * (invalid or expired token) clears the session too, which sends the user to the sign-in page (see RequireAuth).
 */
export function AuthProvider({ children }: { children?: React.ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(() => {
    const saved = readSession();
    currentToken = saved ? saved.token : null; // set before the first request is made
    setAuthTokenProvider(() => currentToken); // (re)registered on every mount: nothing else may leave the client without it
    return saved;
  });
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const logout = useCallback(() => {
    currentToken = null;
    writeSession(null);
    setSession(null);
  }, []);

  // A 401 means the token is invalid or expired. A layout effect runs before any child's data loading starts.
  useLayoutEffect(() => {
    setUnauthorizedHandler(() => {
      if (!sessionRef.current) return; // already signed out
      logout();
      setSessionExpired(true);
    });
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  const login = useCallback(async (username: string, password: string): Promise<LoginResult> => {
    const result = await loginRequest({ username, password }); // may throw ApiError (offline, timeout, ...)
    if (result.ok && result.user) {
      const next: StoredSession = { user: result.user, token: result.token ?? null, expiresAt: result.expiresAt ?? null };
      currentToken = next.token;
      writeSession(next);
      setSession(next);
      setSessionExpired(false);
    }
    return { ok: result.ok, user: result.user, message: result.message };
  }, []);

  // Sessions saved before the role existed: ask who the token belongs to once (GET /users/me). Failures are ignored here:
  // a 401 is handled globally, and without a role the admin screens simply stay hidden.
  const needsProfile = Boolean(session && session.token && !session.user.role);
  useEffect(() => {
    if (!needsProfile) return undefined;
    let cancelled = false;
    getCurrentUser()
      .then((profile: Record<string, unknown> | null) => {
        if (cancelled || !profile) return;
        const { password, ...safe } = profile;
        setSession((s) => {
          if (!s) return s;
          const next = { ...s, user: { ...s.user, ...safe } as AuthUser };
          writeSession(next);
          return next;
        });
      })
      .catch(() => { /* keep the session as it is */ });
    return () => { cancelled = true; };
  }, [needsProfile]);

  const user = session ? session.user : null;
  const role = user ? user.role : undefined;
  const value = useMemo<AuthValue>(
    () => ({ user, isAuthenticated: Boolean(user), role, isAdmin: role === 'admin' || role === 'superadmin', roleLoading: needsProfile, sessionExpired, login, logout }),
    [user, role, needsProfile, sessionExpired, login, logout]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
